import React, {useEffect, useRef} from 'react'

import L from '../../../components/map/leaflet.js'
import {createTileLayer, defaultCenter, defaultZoom, fitBoundsSafe} from '../../../components/map/setup.js'
import {validPosition} from '../../../components/map/geometry.js'
import useLeafletMap from '../../../components/map/hooks/useLeafletMap.js'
import useLayerGroup from '../../../components/map/hooks/useLayerGroup.js'
import MapControls from '../../../components/map/controls/MapControls.js'
import MapAttribution from '../../../components/map/controls/MapAttribution.js'
import escapeHtml from '../../../../utils/escapeHtml.js'
import {PickupPoint} from '../../../../server/functions/parcels/parcels.types.js'
import {BORDER, CARRIER_LOGOS, carrierDisplayName} from './ui.js'

const DAYS = [
    ['monday', 'Maandag'],
    ['tuesday', 'Dinsdag'],
    ['wednesday', 'Woensdag'],
    ['thursday', 'Donderdag'],
    ['friday', 'Vrijdag'],
    ['saturday', 'Zaterdag'],
    ['sunday', 'Zondag']
]

// Our zoom buttons and credits instead of Leaflet's controls, as on the other maps
const mapOptions: L.MapOptions = {attributionControl: false, zoomControl: false}

interface PickupPointMapProps {
    pickupPoints: PickupPoint[]
    /** Only the pickup points of this carrier, all without */
    carrier?: string
    /** The consumer's address, where the map starts */
    center?: {lat?: number, lng?: number}
}

// Shows the pickup points (servicepunten) of the selected carrier around the consumer's address
export default function PickupPointMap({pickupPoints, carrier, center}: PickupPointMapProps) {
    const containerRef = useRef<HTMLDivElement>(null)

    const map = useLeafletMap(containerRef, mapOptions, (map) => {
        map.setView(validPosition(center) || defaultCenter, defaultZoom)
        createTileLayer({updateWhenZooming: true, updateWhenIdle: L.Browser.mobile}).addTo(map)
    })

    const markers = useLayerGroup(map)

    useEffect(() => {
        if (!map || !markers) {
            return
        }

        markers.clearLayers()
        const positions: L.LatLngLiteral[] = []

        for (const pickupPoint of pickupPoints) {
            const position = validPosition(pickupPoint.location)

            if ((carrier && pickupPoint.carrier !== carrier) || !position) {
                continue
            }

            const logo = CARRIER_LOGOS[carrierDisplayName(pickupPoint.carrier)]
            const icon = logo ? L.icon({iconUrl: logo, iconSize: [36, 36]}) : new L.Icon.Default()

            L.marker(position, {icon}).bindTooltip(tooltip(pickupPoint)).addTo(markers)
            positions.push(position)
        }

        fitBoundsSafe(map, positions, {padding: [24, 24], maxZoom: 15, pointZoom: 15})
    }, [map, markers, pickupPoints, carrier])

    return (
        <div style={{position: 'relative', zIndex: 0, marginTop: 16, borderRadius: 12, overflow: 'hidden', border: `1px solid ${BORDER}`}}>
            <div ref={containerRef} style={{height: 280, width: '100%'}} />
            <MapControls map={map} />
            <MapAttribution />
        </div>
    )
}

function tooltip(pickupPoint: PickupPoint) {
    const {address, openingHours = {}} = pickupPoint

    return `
        <div style='max-width: 260px'>
            <b>${escapeHtml(address.name)}</b><br>
            ${escapeHtml(`${address.street} ${address.nr}${address.addition ? ` ${address.addition}` : ''}`)}<br>
            ${escapeHtml(`${address.postalCode} ${address.city}`)}
            <hr style='margin: 6px 0'>
            ${DAYS.map(([key, day]) => `${day}: ${escapeHtml(openingHours[key] || '-')}`).join('<br>')}
        </div>
    `
}
